import React, { useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import { 
  Tab, Tabs, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Select, MenuItem, FormControl, InputLabel, FormHelperText, CircularProgress,
  Pagination, Stack, Alert, Typography
} from '@mui/material';
import { toast } from 'react-toastify';
import adminService from '../../services/adminService';
import AddDoctorModal from './AddDoctorModal';
import '../../styles/components/admin/AdminLayout.css';
import '../../styles/components/admin/UserManagement.css';
import '../../styles/components/admin/ProfessionalVerification.css';
// Add this with your other imports at the top
import systemLogo from '../../assets/logo/neurolex_logo_new2.png';

// Import icons from assets
import PlusIcon from '../../assets/icons/appointment_icon.svg';
import SearchIcon from '../../assets/icons/search_icon.svg';
import EditIcon from '../../assets/icons/edit_icon.svg';
import TrashIcon from '../../assets/icons/delete_icon.svg';
import EyeIcon from '../../assets/icons/view_icon.svg';
import DownloadIcon from '../../assets/icons/appointment_icon.svg';
import CheckCircleIcon from '../../assets/icons/view_icon.svg';
import XCircleIcon from '../../assets/icons/DoctorManagement_Icon.svg';

const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

// Tab panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// A11y props for tabs
function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const ProfessionalVerification = () => {
  const navigate = useNavigate();
  
  // State variables
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [approvedDoctors, setApprovedDoctors] = useState([]);
  const [rejectedDoctors, setRejectedDoctors] = useState([]);
  const [currentDoctor, setCurrentDoctor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    counts: { pendingDoctors: 0, approvedDoctors: 0, rejectedDoctors: 0 }
  });
  
  // Tabs
  const [tabValue, setTabValue] = useState(0);
  
  // Modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showAddDoctorModal, setShowAddDoctorModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [approving, setApproving] = useState(false);
  
  // Form fields
  const [verificationStatus, setVerificationStatus] = useState('approved');
  const [verificationNotes, setVerificationNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [formError, setFormError] = useState('');
  
  // DELETE STATES
  const [doctorToDelete, setDoctorToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Filters for registered professionals
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [specializationFilter, setSpecializationFilter] = useState('All');

  const [filterLoading, setFilterLoading] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  // Pagination
  const [currentTabPage, setCurrentTabPage] = useState(1);
  const [currentApprovedPage, setCurrentApprovedPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [tabTotalPages, setTabTotalPages] = useState(1);
  const [approvedTotalPages, setApprovedTotalPages] = useState(1);
  
  // Available specializations
  const [specializations, setSpecializations] = useState([]);
  
  // Load data
  useEffect(() => {
    loadData();
  }, [currentTabPage, currentApprovedPage, debouncedSearchTerm, statusFilter, specializationFilter]);

  useEffect(() => {
  const fetchSpecialties = async () => {
    try {
      const response = await adminService.getUniqueSpecialties();
      if (response.success) {
        setSpecializations(response.data);
      }
    } catch (error) {
      console.error('Error fetching specialties:', error);
      // Fallback to default if API fails
      setSpecializations(['Psychiatrist', 'Psychologist', 'Other']);
    }
  };
  
  fetchSpecialties();
}, []);

   const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentApprovedPage(1); // Reset to first page when searching
  };
  
  const loadData = async () => {

  if (approvedDoctors.length > 0) {
    setFilterLoading(true);
  } else {
    setLoading(true);
  }
  
  try {
    // Get verification stats
    const statsRes = await adminService.getDoctorVerificationStats();
    setStats(statsRes.data);
    
    // Get pending doctors
    const pendingRes = await adminService.getPendingDoctors({
      page: tabValue === 0 ? currentTabPage : 1,
      limit: itemsPerPage
    });
    setPendingDoctors(pendingRes.data);
    
    if (pendingRes.pagination) {
      setTabTotalPages(pendingRes.pagination.pages);
    }
    
    // Debug log
    console.log('🔍 Search Parameters:', {
      search: debouncedSearchTerm,
      status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
      specialization: specializationFilter !== 'All' ? specializationFilter : undefined,
      page: currentApprovedPage,
      limit: itemsPerPage
    });
    
    // Get approved doctors with filters - FIXED LINE
    const approvedRes = await adminService.getApprovedDoctors({
      page: currentApprovedPage,
      limit: itemsPerPage,
      search: debouncedSearchTerm || undefined, // ✅ FIXED: using debouncedSearchTerm
      status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
      specialization: specializationFilter !== 'All' ? specializationFilter : undefined
    });
    
    console.log('✅ API Response:', approvedRes);
    setApprovedDoctors(approvedRes.data);
    
    if (approvedRes.pagination) {
      setApprovedTotalPages(approvedRes.pagination.pages);
    }
    
    // Get rejected doctors
    const rejectedRes = await adminService.getRejectedDoctors({
      page: tabValue === 1 ? currentTabPage : 1,
      limit: itemsPerPage
    });
    setRejectedDoctors(rejectedRes.data);
    
    if (rejectedRes.pagination && tabValue === 1) {
      setTabTotalPages(rejectedRes.pagination.pages);
    }
    
  } catch (error) {
    console.error('Error loading verification data:', error);
    
    if (error.response) {
      const status = error.response.status;
      if (status === 401) {
        toast.error('Session expired. Please log in again.');
      } else if (status === 403) {
        toast.error('You do not have permission to access this page.');
      } else {
        toast.error(`Error: ${error.response.data?.message || 'Failed to load verification data'}`);
      }
    } else {
      toast.error('Network error. Please check your connection.');
    }
  } finally {
    setLoading(false);
    setFilterLoading(false);
  }
};
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setCurrentTabPage(1);
  };
  
  const viewDoctorDetails = (doctorId, tenantId, isPending = false) => {
  if (!doctorId) {
    console.error('❌ No doctorId provided!');
    toast.error('Cannot view doctor: ID is missing');
    return;
  }
  console.log('🔍 Navigating to doctor details:', doctorId);
  
  // If pending/rejected, use pending route with verification buttons
  if (isPending) {
    navigate(`/admin/professionals/pending/${doctorId}`);
  } else {
    // Approved doctors use regular route with tabs
    navigate(`/admin/professionals/${doctorId}`);
  }
};

  const handleEditDoctor = (doctorId, tenantId) => {
  console.log('🔧 Navigating to edit doctor:', doctorId);
  // Navigate to the pending route with edit mode - REUSING the pending component for editing
  navigate(`/admin/professionals/pending/${doctorId}?mode=edit`);
};
  
  const openVerifyModal = (doctor) => {
    setCurrentDoctor(doctor);
    setVerificationStatus('approved');
    setVerificationNotes('');
    setRejectionReason('');
    setFormError('');
    setShowVerifyModal(true);
  };
  
  const handleVerifyDoctor = async (e) => {
    e.preventDefault();
    setFormError('');
    
    if (verificationStatus === 'rejected' && !rejectionReason.trim()) {
      setFormError('Rejection reason is required');
      return;
    }
    
    setApproving(true);
    
    try {
      const verificationData = {
        verificationStatus,
        verificationNotes,
        rejectionReason: verificationStatus === 'rejected' ? rejectionReason : ''
      };
      
      console.log('Verifying doctor:', {
        doctorId: currentDoctor._id,
        status: verificationStatus,
        tenantId: currentDoctor.tenantId || null
      });
      
      const tenantId = currentDoctor.tenantId || null;
      
      await adminService.verifyDoctor(currentDoctor._id, verificationData, tenantId);
      
      toast.success(`Doctor ${verificationStatus === 'approved' ? 'approved' : 'rejected'} successfully`);
      setShowVerifyModal(false);
      
      loadData();
      
    } catch (error) {
      console.error('Error verifying doctor:', error);
      
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          toast.error('Doctor not found. They may have been deleted or moved to a different tenant.');
        } else {
          toast.error(`Failed to update doctor verification status: ${error.response.data?.message || 'Server error'}`);
        }
      } else {
        toast.error('Network error. Please check your connection and try again.');
      }
    } finally {
      setApproving(false);
    }
  };

  // DELETE DOCTOR FUNCTIONS
  const openDeleteModal = (doctor) => {
    console.log('🗑️ Opening delete modal for doctor:', doctor);
    setDoctorToDelete(doctor);
    setShowDeleteModal(true);
  };

  const handleDeleteDoctor = async () => {
    if (!doctorToDelete) return;
    
    setDeleting(true);
    
    try {
      console.log('🗑️ Deleting doctor:', doctorToDelete._id);
      
      await adminService.deleteDoctor(doctorToDelete._id, doctorToDelete.tenantId);
      
      toast.success(`Doctor ${doctorToDelete.firstName} ${doctorToDelete.lastName} deleted successfully`);
      setShowDeleteModal(false);
      setDoctorToDelete(null);
      
      // Refresh the data
      loadData();
      
    } catch (error) {
      console.error('❌ Error deleting doctor:', error);
      
      if (error.response) {
        const status = error.response.status;
        if (status === 404) {
          toast.error('Doctor not found. They may have already been deleted.');
        } else {
          toast.error(`Failed to delete doctor: ${error.response.data?.message || 'Server error'}`);
        }
      } else {
        toast.error('Network error. Please check your connection and try again.');
      }
    } finally {
      setDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    if (!deleting) {
      setShowDeleteModal(false);
      setDoctorToDelete(null);
    }
  };
  
  // Format date to local string
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Helper function to get status color
  const getStatusColor = (status) => {
    if (!status) return 'default';
    
    const statusLower = String(status).toLowerCase();
    
    switch(statusLower) {
      case 'approved':
        return 'success';
      case 'rejected':
        return 'error';
      case 'pending':
      default:
        return 'warning';
    }
  };
  
  // Helper function to get status display text
  const getStatusDisplayText = (status) => {
    if (!status) return 'PENDING';
    return String(status).toUpperCase();
  };
  
  // Pagination handlers
  const paginateTab = (pageNumber) => {
    setCurrentTabPage(pageNumber);
  };
  
  const paginateApproved = (pageNumber) => {
    setCurrentApprovedPage(pageNumber);
  };
  
  // Handle add new doctor
  const handleAddDoctor = () => {
    console.log('Opening Add Doctor modal');
    setShowAddDoctorModal(true);
  };

  // Handle when a doctor is successfully added
  const handleDoctorAdded = () => {
    console.log('Doctor added successfully, refreshing data...');
    loadData(); // Refresh the data to show the new doctor
  };
  
  if (loading && !pendingDoctors.length && !approvedDoctors.length && !rejectedDoctors.length) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>Loading verification data...</Typography>
      </Box>
    );
  }
  
  return (
    <div className="professional-verification">
      <div className="pv-dashboard-header">
        <h1 className="pv-dashboard-title">Doctor Management</h1>
        <p className="pv-dashboard-subtitle">Manage mental health professionals and verify applications.</p>
      </div>

      {/* Doctor Application Under Review Section */}
      <div className="pv-dashboard-section">
        <h2 className="pv-admin-section-title">Doctor Application Under Review</h2>
        
        {/* Tab Navigation */}
        <div className="pv-tab-navigation">
          <button 
            className={`pv-tab-button ${tabValue === 0 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 0)}
          >
            PENDING ({pendingDoctors.length})
          </button>
          <button 
            className={`pv-tab-button ${tabValue === 1 ? 'active' : ''}`}
            onClick={(e) => handleTabChange(e, 1)}
          >
            REJECTED ({rejectedDoctors.length})
          </button>
        </div>
        
        {/* Tab Content */}
        {tabValue === 0 && (
          <div className="pv-tab-content">
            {pendingDoctors.length === 0 ? (
              <div className="pv-no-data-message">
                No pending doctor applications
              </div>
            ) : (
              <div className="pv-custom-table">
                <div className="pv-table-row pv-header-row">
                  <div className="pv-table-cell">Name</div>
                  <div className="pv-table-cell">Email</div>
                  <div className="pv-table-cell">Specialization</div>
                  <div className="pv-table-cell">Registration Date</div>
                  <div className="pv-table-cell">Actions</div>
                </div>
                
                {pendingDoctors.map((doctor, index) => (
                  <div 
                    key={doctor._id} 
                    className={`pv-table-row ${index % 2 === 0 ? 'pv-odd-row' : 'pv-even-row'}`}
                  >
                    <div className="pv-table-cell">
                      <div className="pv-user-name-cell">
                        <div className="pv-user-avatar">
                          {doctor.profilePicture && doctor.profilePicture !== 'default-profile.png' && doctor.profilePicture.startsWith('http') ? (
                            <img src={doctor.profilePicture} alt={`${doctor.firstName} ${doctor.lastName}`} />
                          ) : (
                            <div className="pv-user-initials">
                              {doctor.firstName?.[0]?.toUpperCase()}{doctor.lastName?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="pv-user-info">
                          <div className="pv-user-fullname">
                            {doctor.firstName} {doctor.lastName}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pv-table-cell">{doctor.email}</div>
                    <div className="pv-table-cell">{doctor.specialization || 'Not specified'}</div>
                    <div className="pv-table-cell">{formatDate(doctor.createdAt)}</div>
                    <div className="pv-table-cell">
                      <div className="pv-action-buttons">
                        <button 
                              className="pv-btn-icon view"
                              onClick={() => viewDoctorDetails(doctor._id, doctor.tenantId, true)}
                              title="View Doctor Details"
                            >
                          <div className="pv-view-icon"></div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {tabTotalPages > 1 && (
              <div className="pv-pagination">
                <button 
                  className="pv-page-nav prev"
                  onClick={() => paginateTab(currentTabPage - 1)}
                  disabled={currentTabPage === 1}
                >
                  &lt;
                </button>
                
                <div className="pv-page-numbers">
                  {[...Array(tabTotalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`pv-page-number ${currentTabPage === index + 1 ? 'active' : ''}`}
                      onClick={() => paginateTab(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                <button 
                  className="pv-page-nav next"
                  onClick={() => paginateTab(currentTabPage + 1)}
                  disabled={currentTabPage === tabTotalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        )}
        
        {tabValue === 1 && (
          <div className="pv-tab-content">
            {rejectedDoctors.length === 0 ? (
              <div className="pv-no-data-message">
                No rejected doctors found
              </div>
            ) : (
              <div className="pv-custom-table">
                <div className="pv-table-row pv-header-row">
                  <div className="pv-table-cell">Name</div>
                  <div className="pv-table-cell">Email</div>
                  <div className="pv-table-cell">Specialization</div>
                  <div className="pv-table-cell">Registration Date</div>
                  <div className="pv-table-cell">Actions</div>
                </div>
                
                {rejectedDoctors.map((doctor, index) => (
                  <div 
                    key={doctor._id} 
                    className={`pv-table-row ${index % 2 === 0 ? 'pv-odd-row' : 'pv-even-row'}`}
                  >
                    <div className="pv-table-cell">
                      <div className="pv-user-name-cell">
                        <div className="pv-user-avatar">
                          {doctor.profilePicture && doctor.profilePicture !== 'default-profile.png' && doctor.profilePicture.startsWith('http') ? (
                            <img src={doctor.profilePicture} alt={`${doctor.firstName} ${doctor.lastName}`} />
                          ) : (
                            <div className="pv-user-initials">
                              {doctor.firstName?.[0]?.toUpperCase()}{doctor.lastName?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="pv-user-info">
                          <div className="pv-user-fullname">
                            {doctor.firstName} {doctor.lastName}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="pv-table-cell">{doctor.email}</div>
                    <div className="pv-table-cell">{doctor.specialization || 'Not specified'}</div>
                    <div className="pv-table-cell">{formatDate(doctor.verificationDate)}</div>
                    <div className="pv-table-cell">
                      <div className="pv-action-buttons">
                        <button 
                              className="pv-btn-icon view"
                              onClick={() => viewDoctorDetails(doctor._id, doctor.tenantId, true)}
                              title="View Doctor Details"
                            >
                          <div className="pv-view-icon"></div>
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {tabTotalPages > 1 && (
              <div className="pv-pagination">
                <button 
                  className="pv-page-nav prev"
                  onClick={() => paginateTab(currentTabPage - 1)}
                  disabled={currentTabPage === 1}
                >
                  &lt;
                </button>
                
                <div className="pv-page-numbers">
                  {[...Array(tabTotalPages)].map((_, index) => (
                    <button
                      key={index}
                      className={`pv-page-number ${currentTabPage === index + 1 ? 'active' : ''}`}
                      onClick={() => paginateTab(index + 1)}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
                
                <button 
                  className="pv-page-nav next"
                  onClick={() => paginateTab(currentTabPage + 1)}
                  disabled={currentTabPage === tabTotalPages}
                >
                  &gt;
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Registered Mental Health Professionals Section */}
      <div className="pv-registered-professionals-section">
        <h2 className="pv-admin-section-title">Registered Mental Health Professionals</h2>
        
        {/* Search and Filter Bar */}
       <div className="pv-admin-actions-bar">
  <div className="pv-top-actions-row">
    <div className="pv-left-actions">
      <div className="pv-search-box" style={{ position: 'relative' }}>
        <img src={SearchIcon} alt="Search" className="pv-search-icon" />
        <input 
          type="text" 
          placeholder="Search by name, email, or specialization" 
          value={searchTerm} 
          onChange={handleSearchChange}
        />
        {searchTerm && (
          <button 
            className="pv-clear-search-btn"
            onClick={() => {
              setSearchTerm('');
              setCurrentApprovedPage(1);
            }}
            title="Clear search"
            style={{
              position: 'absolute',
              right: '10px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: '#999',
              padding: '0 5px'
            }}
          >
            ×
          </button>
        )}
      </div>
      
      <button className="pv-Add-Patient-Button" onClick={handleAddDoctor}>
        <svg className="pv-button-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Add a Doctor</span>
      </button>
    </div>
    
    <div className="pv-show-entries">
      <span className="pv-show-label">Show</span>
      <div className="pv-show-number">10</div>
    </div>
  </div>
  
  <div className="pv-bottom-actions-row">
    <div className="pv-filter-box">
      <div className="pv-filter-item">
        <label htmlFor="status-filter">Status</label>
        <select 
          id="status-filter" 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>
      
      <div className="pv-filter-item">
        <label htmlFor="specialization-filter">Specialization</label>
        <select 
          id="specialization-filter" 
          value={specializationFilter} 
          onChange={(e) => setSpecializationFilter(e.target.value)}
        >
          <option value="All">All</option>
          {specializations.map(spec => (
            <option key={spec} value={spec}>{spec}</option>
          ))}
        </select>
      </div>
    </div>
    
    <button 
  className="pv-Export-PDF-Button"
  onClick={() => {
    try {
      const doc = new jsPDF();
      
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Registered Mental Health Professionals', 14, 20);
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on ${new Date().toLocaleDateString()}`, 14, 28);
      
      doc.setFontSize(11);
      doc.text(`Total Doctors: ${approvedDoctors.length}`, 14, 36);
      
      const tableData = approvedDoctors.map(doctor => [
        `${doctor.firstName} ${doctor.lastName}`,
        doctor.email,
        doctor.specialty || 'Not specified',
        doctor.tenantName || 'N/A',
        new Date(doctor.verificationDate).toLocaleDateString(),
        doctor.accountStatus === 'active' ? 'Active' : 
        doctor.accountStatus === 'suspended' ? 'Suspended' : 'Pending'
      ]);
      
      autoTable(doc, {
        startY: 42,
        head: [['Name', 'Email', 'Specialty', 'Clinic', 'Verified Date', 'Status']],
        body: tableData,
        theme: 'striped',
        headStyles: {
          fillColor: [91, 140, 126],
          textColor: 255,
          fontStyle: 'bold',
          fontSize: 10
        },
        styles: {
          fontSize: 9,
          cellPadding: 3
        },
        columnStyles: {
          0: { cellWidth: 35 },
          1: { cellWidth: 45 },
          2: { cellWidth: 30 },
          3: { cellWidth: 30 },
          4: { cellWidth: 25 },
          5: { cellWidth: 20 }
        },
        margin: { top: 42, left: 14, right: 14 },
        didDrawPage: function (data) {
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            doc.internal.pageSize.width / 2,
            doc.internal.pageSize.height - 10,
            { align: 'center' }
          );
        }
      });
      
      const fileName = `verified-doctors-${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Failed to export PDF');
    }
  }}
>
  <svg className="pv-button-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" fill="currentColor"/>
    <path d="M14 2V8H20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
  <span>Export PDF</span>
</button>
  </div>
</div>
        
        {/* Approved Doctors Table */}
                    
            {filterLoading ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '60px 20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                margin: '20px 0'
              }}>
                <CircularProgress size={40} style={{ color: '#5B8C7E' }} />
                <Typography variant="body1" sx={{ mt: 2, color: '#666' }}>
                  {searchTerm ? 'Searching doctors...' : 
                  statusFilter !== 'All' || specializationFilter !== 'All' ? 
                  'Filtering doctors...' : 'Loading doctors...'}
                </Typography>
              </div>
            ) : approvedDoctors.length === 0 ? (
              <div className="pv-no-data-message">
                No approved doctors found
              </div>
            ) : (
              <div className="pv-custom-table">
            <div className="pv-table-row pv-header-row">
              <div className="pv-table-cell">Name</div>
              <div className="pv-table-cell">Email</div>
              <div className="pv-table-cell">Specialization</div>
              <div className="pv-table-cell">Verification Date</div>
              <div className="pv-table-cell">Status</div>
              <div className="pv-table-cell">Actions</div>
            </div>
            
            {approvedDoctors.map((doctor, index) => (
              <div 
                key={doctor._id} 
                className={`pv-table-row ${index % 2 === 0 ? 'pv-odd-row' : 'pv-even-row'}`}
              >
                <div className="pv-table-cell">
                  <div className="pv-user-name-cell">
                    <div className="pv-user-avatar">
                      {doctor.profilePicture && doctor.profilePicture !== 'default-profile.png' && doctor.profilePicture.startsWith('http') ? (
                        <img src={doctor.profilePicture} alt={`${doctor.firstName} ${doctor.lastName}`} />
                      ) : (
                        <div className="pv-user-initials">
                          {doctor.firstName?.[0]?.toUpperCase()}{doctor.lastName?.[0]?.toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="pv-user-info">
                      <div className="pv-user-fullname">
                        {doctor.firstName} {doctor.lastName}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="pv-table-cell">{doctor.email}</div>
                <div className="pv-table-cell">{doctor.specialty || 'Not specified'}</div>
                <div className="pv-table-cell">{formatDate(doctor.verificationDate)}</div>
                <div className="pv-table-cell">
                  <span className={`pv-status-badge ${doctor.accountStatus || 'active'}`}>
                    {doctor.accountStatus === 'inactive' ? 'INACTIVE' : 'ACTIVE'}
                  </span>
                </div>
                <div className="pv-table-cell">
                  <div className="pv-action-buttons">
                    <button 
                      className="pv-btn-icon view"
                      onClick={() => viewDoctorDetails(doctor._id, doctor.tenantId)}
                      title="View Doctor Details"
                    >
                      <div className="pv-view-icon"></div>
                    </button>
                    <button 
                      className="pv-btn-icon edit"
                      onClick={() => handleEditDoctor(doctor._id, doctor.tenantId)}
                      title="Edit Doctor"
                    >
                      <div className="pv-edit-icon"></div>
                    </button>
                    <button 
                      className="pv-btn-icon delete"
                      onClick={() => openDeleteModal(doctor)}
                      title="Delete Doctor"
                    >
                      <div className="pv-delete-icon"></div>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {approvedTotalPages > 1 && (
          <div className="pv-pagination">
            <button 
              className="pv-page-nav prev"
              onClick={() => paginateApproved(currentApprovedPage - 1)}
              disabled={currentApprovedPage === 1}
            >
              &lt;
            </button>
            
            <div className="pv-page-numbers">
              {[...Array(approvedTotalPages)].map((_, index) => (
                <button
                  key={index}
                  className={`pv-page-number ${currentApprovedPage === index + 1 ? 'active' : ''}`}
                  onClick={() => paginateApproved(index + 1)}
                >
                  {index + 1}
                </button>
              ))}
            </div>
            
            <button 
              className="pv-page-nav next"
              onClick={() => paginateApproved(currentApprovedPage + 1)}
              disabled={currentApprovedPage === approvedTotalPages}
            >
              &gt;
            </button>
          </div>
        )}
      </div>
      
      {/* Verification Dialog */}
      <Dialog
        open={showVerifyModal}
        onClose={() => !approving && setShowVerifyModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Verify Doctor</DialogTitle>
        <DialogContent>
          {currentDoctor && (
            <Box component="form" noValidate onSubmit={handleVerifyDoctor} sx={{ mt: 1 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                You are about to verify: <strong>{currentDoctor.firstName} {currentDoctor.lastName}</strong>
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="verification-status-label">Verification Status</InputLabel>
                <Select
                  labelId="verification-status-label"
                  id="verification-status"
                  value={verificationStatus}
                  label="Verification Status"
                  onChange={(e) => setVerificationStatus(e.target.value)}
                  required
                  disabled={approving}
                >
                  <MenuItem value="approved">Approve</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                margin="normal"
                fullWidth
                id="verification-notes"
                label="Verification Notes (Optional)"
                multiline
                rows={2}
                value={verificationNotes}
                onChange={(e) => setVerificationNotes(e.target.value)}
                placeholder="Add any notes about this verification"
                helperText="These notes are for admin purposes only."
                disabled={approving}
              />
              
              {verificationStatus === 'rejected' && (
                <TextField
                  margin="normal"
                  fullWidth
                  id="rejection-reason"
                  label="Rejection Reason"
                  multiline
                  rows={3}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Explain why this doctor is being rejected"
                  required={verificationStatus === 'rejected'}
                  error={!!formError}
                  helperText={formError || "This reason will be sent to the doctor."}
                  disabled={approving}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVerifyModal(false)} color="inherit" disabled={approving}>
            Cancel
          </Button>
          <Button 
            onClick={handleVerifyDoctor}
            color={verificationStatus === 'approved' ? 'success' : 'error'}
            variant="contained"
            disabled={approving}
            startIcon={
              approving ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {approving ? 'Processing...' : verificationStatus === 'approved' ? 'Approve Doctor' : 'Reject Doctor'}
          </Button>
        </DialogActions>
      </Dialog>

     <Dialog
  open={showDeleteModal}
  onClose={closeDeleteModal}
  maxWidth="sm"
  fullWidth
   PaperProps={{
    sx: {
      borderRadius: '10px'  
    }
  }}
>
  {/* Logo above the title */}
  <Box sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
    <img 
      src={systemLogo} 
      alt="System Logo" 
      style={{ 
        width: '80px', 
        height: '80px', 
        objectFit: 'contain'
      }} 
    />
  </Box>
  
  <DialogTitle sx={{ 
  textAlign: 'center', 
  color: '#274137', 
  pt: 1,
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 700,
  fontSize: '30px'  // Optional: adjust size
}}>
  DELETE DOCTOR ACCOUNT?
</DialogTitle>
  
  <DialogContent>
    {doctorToDelete && (
      <Box sx={{ textAlign: 'center', py: 2 }}>
        {/* Doctor Info Box */}
        <Box sx={{ 
          backgroundColor: '#F6FBF9', 
          border: '1px solid #A7D7C5',
          padding: '16px', 
          borderRadius: '10px', 
          mb: 2 
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            fontFamily: 'IBM Plex Mono, monospace'
          }}>
            {doctorToDelete.firstName} {doctorToDelete.lastName}
          </Typography>
          <Typography variant="body2" color="#000000ff" sx={{
            fontFamily: 'IBM Plex Mono, monospace'
          }}>
            {doctorToDelete.email}
          </Typography>
        </Box>
        
        <Typography variant="body1" sx={{
          fontFamily: 'Poppins, sans-serif',
          color: '#274137'
        }}>
          Deleting this account is permanent. The professional will lose all access to patient data and records.
        </Typography>
      </Box>
    )}
  </DialogContent>
  
  <DialogActions sx={{ 
  justifyContent: 'center', 
  gap: 2, 
  pb: 3,
  px: 3  // Add horizontal padding
}}>
  <Button 
    onClick={closeDeleteModal} 
    variant="outlined"
    color="inherit"
    disabled={deleting}
    sx={{ 
      flex: 1,  // ✅ Changed from width to flex
      color: '#274137',
      borderColor: '#274137',
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '18px',
      fontWeight: 700,
      '&:hover': {
        borderColor: '#274137',
        backgroundColor: '#ffffffff'
      }
    }}
  >
    Cancel
  </Button>
  <Button 
    onClick={handleDeleteDoctor}
    variant="contained"
    color="error"
    disabled={deleting}
    startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : null}
    sx={{ 
      flex: 1,  // ✅ Changed from minWidth to flex
      backgroundColor: '#ff6b6b',
      fontFamily: 'IBM Plex Mono, monospace',
      fontSize: '18px',
      fontWeight: 700,
      '&:hover': {
        backgroundColor: '#ff5252'
      }
    }}
  >
    {deleting ? 'Deleting...' : 'Yes, Delete'}
  </Button>
</DialogActions>
</Dialog>

      {/* Add Doctor Modal */}
      <AddDoctorModal
        isOpen={showAddDoctorModal}
        onClose={() => setShowAddDoctorModal(false)}
        onDoctorAdded={handleDoctorAdded}
      />
    </div>
  );
};

export default ProfessionalVerification;